import type { TerrainSampler, TilePos } from "../world/types";
import type { CameraPose } from "./controller";

/*
 * The ray under the crosshair: which tile is the player pointing at.
 *
 * A grid traversal (Amanatides & Woo) rather than `THREE.Raycaster`, for the
 * reason the Three.js voxel article gives and the rendering brief repeats
 * (`docs/research/rendering/threejs-chunks.md` §4): the scene's geometry is
 * merged, greedy-meshed slabs, so a mesh intersection would name a triangle
 * belonging to a quad that spans a dozen tiles. The world is a grid; the query
 * belongs on the grid.
 *
 * It reads the same {@link TerrainSampler} collision reads, which means it
 * inherits the client's unknown-is-solid rule. That is harmless where it is used
 * — six tiles from a player who is at the centre of a generated interest square
 * — and it is the right failure mode besides: pointing at a tile the world has
 * not generated and asking to dig it produces a command the simulation drops
 * without a word (`WasmSim::queue_dig`), not a wrong answer.
 *
 * Nothing here decides whether a tile *may* be dug. That is a rule about the
 * world and it lives in `sim-core`; this function answers "which tile", the
 * client sends a command, and the simulation says what happened.
 */

/** What the ray hit. */
export interface RayHit {
  /** The first solid tile along the ray. */
  readonly tile: TilePos;

  /**
   * The face it entered through, as a unit tile offset.
   *
   * Unused today. It is the vector a placement verb would add to `tile` to find
   * the empty tile in front of the face, and it costs nothing to report while
   * the traversal already knows which axis it stepped along.
   */
  readonly face: TilePos;
}

/** How far the dig ray reaches, in tiles. Arm's length plus a little. */
export const DIG_RANGE_TILES = 6;

/**
 * Returns the first solid tile along a ray, or `null` within `maxDistance`.
 *
 * `origin` is in world tile units and `direction` need not be normalised — it is
 * normalised here, because a caller that built it from a camera pose has no
 * reason to think about that.
 */
export function raycastVoxels(
  origin: { x: number; y: number; z: number },
  direction: { x: number; y: number; z: number },
  maxDistance: number,
  terrain: TerrainSampler,
): RayHit | null {
  const length = Math.hypot(direction.x, direction.y, direction.z);

  if (length === 0 || !Number.isFinite(length)) {
    return null;
  }

  const dx = direction.x / length;
  const dy = direction.y / length;
  const dz = direction.z / length;

  let x = Math.floor(origin.x);
  let y = Math.floor(origin.y);
  let z = Math.floor(origin.z);

  const stepX = Math.sign(dx);
  const stepY = Math.sign(dy);
  const stepZ = Math.sign(dz);

  // Distance along the ray to the next boundary on each axis, and the distance
  // between boundaries. A zero component gives `Infinity`, which is the right
  // answer — that axis never steps — and is why this arithmetic is left to the
  // IEEE rules rather than special-cased.
  let nextX = boundaryDistance(origin.x, dx);
  let nextY = boundaryDistance(origin.y, dy);
  let nextZ = boundaryDistance(origin.z, dz);

  const deltaX = Math.abs(1 / dx);
  const deltaY = Math.abs(1 / dy);
  const deltaZ = Math.abs(1 / dz);

  let face: TilePos = { x: 0, y: 0, z: 0 };

  // The tile the eye is inside is tested first: a player standing with their
  // head in a tile they just dug into should be able to dig it again.
  if (terrain.isSolid(x, y, z)) {
    return { tile: { x, y, z }, face };
  }

  // Bounded by distance, not by iterations: one step crosses at least one tile
  // boundary, so the loop cannot outlive the range.
  while (Math.min(nextX, nextY, nextZ) <= maxDistance) {
    if (nextX <= nextY && nextX <= nextZ) {
      x += stepX;
      nextX += deltaX;
      face = { x: -stepX, y: 0, z: 0 };
    } else if (nextY <= nextZ) {
      y += stepY;
      nextY += deltaY;
      face = { x: 0, y: -stepY, z: 0 };
    } else {
      z += stepZ;
      nextZ += deltaZ;
      face = { x: 0, y: 0, z: -stepZ };
    }

    if (terrain.isSolid(x, y, z)) {
      return { tile: { x, y, z }, face };
    }
  }

  return null;
}

/**
 * Returns the ray a camera pose looks along: the eye, and a unit direction.
 *
 * The same convention `render/scene.ts` builds the camera from — yaw 0 looks
 * along +y, positive pitch looks up — written once more here rather than shared,
 * because the camera's version is a quaternion and this one is a vector, and a
 * shared "forward" would have to be one or the other.
 */
export function rayFromPose(pose: CameraPose): {
  origin: { x: number; y: number; z: number };
  direction: { x: number; y: number; z: number };
} {
  const cosPitch = Math.cos(pose.pitch);

  return {
    origin: { x: pose.x, y: pose.y, z: pose.z },
    direction: {
      x: -Math.sin(pose.yaw) * cosPitch,
      y: Math.cos(pose.yaw) * cosPitch,
      z: Math.sin(pose.pitch),
    },
  };
}

/**
 * Returns the distance along the ray to the first tile boundary on one axis.
 *
 * `Infinity` when the ray does not move along it, which is what makes the
 * traversal's `min` pick one of the other two forever.
 */
function boundaryDistance(from: number, direction: number): number {
  if (direction === 0) {
    return Number.POSITIVE_INFINITY;
  }

  const cell = Math.floor(from);
  const boundary = direction > 0 ? cell + 1 : cell;

  return (boundary - from) / direction;
}
